import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { deleteMyAccount } from "@/lib/account.functions";
import { DiscogsImportCard } from "@/components/settings/DiscogsImportCard";
import { PrivacySettingsCard } from "@/components/settings/PrivacySettingsCard";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";

export const Route = createFileRoute("/settings/account")({
  head: () => ({ meta: [{ title: "Conta — Viny Club" }] }),
  component: SettingsAccount,
});

function SettingsAccount() {
  const { user, isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const deleteAccountFn = useServerFn(deleteMyAccount);

  if (!isAuthenticated) {
    return <p className="text-muted-foreground">Entre para gerenciar sua conta.</p>;
  }

  async function changeEmail() {
    setBusy("email");
    try {
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;
      toast.success("Enviamos um link de confirmação para o novo e-mail");
      setEmail("");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function changePassword() {
    if (password.length < 6) {
      toast.error("Senha precisa ter no mínimo 6 caracteres");
      return;
    }
    setBusy("password");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Senha alterada");
      setPassword("");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function signOutEverywhere() {
    setBusy("signout");
    await supabase.auth.signOut({ scope: "global" });
    navigate({ to: "/", replace: true });
  }

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-border bg-surface p-5">
        <h2 className="font-display text-lg font-bold">E-mail</h2>
        <p className="text-xs text-muted-foreground">E-mail atual: {user?.email}</p>
        <div className="mt-3 flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Novo e-mail"
            className="h-10 flex-1 rounded-lg border border-input bg-background px-3 text-sm"
          />
          <button
            onClick={changeEmail}
            disabled={busy === "email" || !email}
            className="rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar"}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Senha</h2>
        <div className="mt-3 flex gap-2">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Nova senha"
            className="h-10 flex-1 rounded-lg border border-input bg-background px-3 text-sm"
          />
          <button
            onClick={changePassword}
            disabled={busy === "password" || !password}
            className="rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy === "password" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar"}
          </button>
        </div>
      </section>

      {user?.id ? <PrivacySettingsCard userId={user.id} /> : null}

      <DiscogsImportCard />

      <section className="rounded-2xl border border-border bg-surface p-5">
        <h2 className="font-display text-lg font-bold">Sessões</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Encerre sua sessão em todos os dispositivos conectados.
        </p>
        <button
          onClick={signOutEverywhere}
          disabled={busy === "signout"}
          className="mt-3 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold"
        >
          Sair de todos os dispositivos
        </button>
      </section>


      <section className="rounded-2xl border border-border bg-surface p-5">
        <h2 className="font-display text-lg font-bold text-foreground">Excluir conta</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Esta ação é permanente. Sua conta, perfil, coleção, posts e mensagens serão removidos definitivamente da nossa base.
        </p>
        <button
          onClick={() => {
            setDeleteConfirm("");
            setDeleteOpen(true);
          }}
          className="mt-3 rounded-full border border-destructive px-4 py-2 text-sm font-semibold text-destructive bg-transparent"
        >
          Excluir minha conta
        </button>
      </section>

      <ResponsiveDialog open={deleteOpen} onOpenChange={(o: boolean) => { if (!o && busy !== "delete") setDeleteOpen(false); }}>
        <ResponsiveDialogContent desktopClassName="max-w-md p-6" mobileClassName="p-5">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="text-destructive">Excluir conta definitivamente</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Esta ação não pode ser desfeita. Para confirmar, digite{" "}
              <span className="font-mono font-semibold">confirmar</span> no campo abaixo.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <input
            type="text"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder="Digite confirmar"
            className="mt-4 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
            autoFocus
          />
          <ResponsiveDialogFooter className="mt-4 gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              disabled={busy === "delete"}
              className="rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={deleteConfirm !== "confirmar" || busy === "delete"}
              onClick={async () => {
                setBusy("delete");
                try {
                  await deleteAccountFn({ data: { confirm: deleteConfirm } });
                  await supabase.auth.signOut();
                  toast.success("Conta excluída");
                  navigate({ to: "/" });
                } catch (e) {
                  toast.error((e as Error).message);
                } finally {
                  setBusy(null);
                }
              }}
              className="inline-flex items-center gap-2 rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground disabled:opacity-50"
            >
              {busy === "delete" && <Loader2 className="h-4 w-4 animate-spin" />}
              Excluir definitivamente
            </button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </div>
  );
}
